import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import {
  Avatar,
  EmptyIndicator,
  FlexContainer,
  ProgressBar
} from 'components/common';
import { Box } from 'components/common/box';
import { ConversationAnalysisSentimentLabels } from '.';

import type { AchievementDiscussionAnalysisResult } from 'shared-types';
import type { IconProp } from 'components/common/icons/Icon';

interface Props {
  trendAnalysis: AchievementDiscussionAnalysisResult['trendAnalysis'];
}

const ConversationAnalysisTrends = ({ trendAnalysis }: Props) => {
  const { messages, theme } = useReduxState();

  const trendOverallDataByValue: {
    [value: string]: {
      backgroundColor: string;
      color: string;
      icon: IconProp;
      label: string;
    };
  } = {
    declining: {
      backgroundColor: theme.dangerLightColor,
      color: theme.dangerLightTextColor,
      icon: 'arrow-trend-down',
      label: messages.achievementConversationAnalysesTrendDeclining
    },
    improving: {
      backgroundColor: theme.successLightColor,
      color: theme.successLightTextColor,
      icon: 'arrow-trend-up',
      label: messages.achievementConversationAnalysesTrendImproving
    },
    mixed: {
      backgroundColor: theme.warningLightColor,
      color: theme.warningLightTextColor,
      icon: 'up-down',
      label: messages.commonMixed
    },
    stable: {
      backgroundColor: theme.lightGrey,
      color: theme.textColor,
      icon: 'equals',
      label: messages.achievementConversationAnalysesTrendStable
    }
  };

  const overallTrendData = trendOverallDataByValue[trendAnalysis?.overallTrend];

  const summarySegments: {
    icon: IconProp;
    iconBackgroundColor: string;
    iconColor: string;
    items: string[];
    title: string;
  }[] = [
    {
      icon: 'lightbulb',
      iconBackgroundColor: theme.lightGrey,
      iconColor: theme.textColorLight,
      items: trendAnalysis?.keyChanges,
      title: messages.achievementConversationAnalysesKeyFindings
    },
    {
      icon: 'check-circle',
      iconBackgroundColor: theme.successLightColor,
      iconColor: theme.successLightTextColor,
      items: trendAnalysis?.topStrengths,
      title: messages.achievementConversationAnalysesStrengths
    },
    {
      icon: 'exclamation-triangle',
      iconBackgroundColor: theme.dangerLightColor,
      iconColor: theme.dangerLightTextColor,
      items: trendAnalysis?.topConcerns,
      title: messages.achievementConversationAnalysesConcerns
    },
    {
      icon: 'flag',
      iconBackgroundColor: theme.warningLightColor,
      iconColor: theme.warningLightTextColor,
      items: trendAnalysis?.recommendations,
      title: messages.achievementConversationAnalysesRecommendedPriorities
    }
  ];

  return (
    <div className="analysis-trends">
      <Box
        title={messages.achievementConversationAnalysesTrends}
        titleDescription={
          messages.achievementConversationAnalysesTrendsDescription
        }
      >
        <div className="overall">
          <div className="overall-icon">
            <Avatar
              backgroundColor={overallTrendData?.backgroundColor}
              color={overallTrendData?.color}
              fontSize="1.4rem"
              icon={overallTrendData?.icon}
              padding="8px 0"
              size="32px"
            />
          </div>

          <div className="overall-label">
            {`${messages.achievementConversationAnalysesTrendOverall}:`}
          </div>

          <div className="overall-value">{overallTrendData?.label}</div>
        </div>

        <h3 className="subtitle">{messages.commonSummary}</h3>

        <div className="sections">
          {summarySegments.map((section, sectionIndex) => (
            <div className="section" key={sectionIndex}>
              <div className="section-header">
                <div className="section-icon">
                  <Avatar
                    backgroundColor={section.iconBackgroundColor}
                    color={section.iconColor}
                    fontSize="1.1rem"
                    icon={section.icon}
                    padding="7px 0"
                  />
                </div>
                <h4 className="section-title">{section.title}</h4>
              </div>
              {section.items?.length ? (
                <ul className="section-items">
                  {section.items.map((item, itemIndex) => (
                    <li className="section-item" key={itemIndex}>
                      {item}
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="section-empty">{messages.commonNoData}</div>
              )}
            </div>
          ))}
        </div>

        <h3 className="subtitle">
          {messages.achievementConversationAnalysesTrend}
        </h3>

        {Object.keys(trendAnalysis?.trends || {}).length ? (
          <div className="trends">
            {Object.keys(trendAnalysis.trends).map((key) => {
              const trend = trendAnalysis.trends[key];

              return (
                <div className="trend" key={key}>
                  <div className="trend-header">
                    <h4 className="trend-title">{key}</h4>

                    <div className="trend-sentiment">
                      <div className="labels">
                        <ConversationAnalysisSentimentLabels
                          negative={trend.sentiment.negative}
                          neutral={trend.sentiment.neutral}
                          positive={trend.sentiment.positive}
                        />
                      </div>

                      <div className="progress">
                        <ProgressBar
                          progresses={[
                            {
                              color: theme.successColor,
                              progress: trend.sentiment.positive * 0.01
                            },
                            {
                              color: 'rgba(0, 0, 0, 0.4)',
                              progress:
                                (trend.sentiment.positive +
                                  trend.sentiment.neutral) *
                                0.01
                            },
                            {
                              color: theme.dangerColor,
                              progress:
                                (trend.sentiment.positive +
                                  trend.sentiment.neutral +
                                  trend.sentiment.negative) *
                                0.01
                            }
                          ]}
                          progressStyle={{ borderRadius: 20, height: 6 }}
                        />
                      </div>
                    </div>
                  </div>

                  {trend.insights.length ? (
                    <div className="insights">
                      {trend.insights.map((insight, insightKey) => (
                        <div className="insight" key={insightKey}>
                          <div className="insight-description">
                            {insight.insight}
                          </div>

                          <div className="insight-frequency">
                            <ProgressBar
                              color={theme.chartMainColor}
                              label={
                                <FlexContainer
                                  style={{
                                    gap: 20,
                                    justifyContent: 'space-between'
                                  }}
                                >
                                  <div>
                                    {
                                      messages.achievementConversationAnalysesFrequency
                                    }
                                  </div>
                                  <div>{`${insight.prevalence}%`}</div>
                                </FlexContainer>
                              }
                              labelStyle={{
                                color: theme.textColorLighter,
                                marginBottom: 3
                              }}
                              progress={insight.prevalence * 0.01}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyIndicator
            icon="chart-line"
            size="small"
            title={messages.achievementConversationAnalysesTrendsNone}
          />
        )}
      </Box>

      <style jsx>{`
        .overall {
          align-items: center;
          display: flex;
          gap: 10px;
        }
        .overall-label {
          color: ${theme.textColor};
          font-size: 1.6rem;
          font-weight: 400;
        }
        .overall-value {
          color: ${theme.textColor};
          font-size: 1.6rem;
          font-weight: 700;
        }
        .subtitle {
          color: ${theme.textColorLight};
          font-size: 1.6rem;
          margin: 50px 0 0;
          padding: 0 5px;
        }
        .sections {
          display: flex;
          flex-wrap: wrap;
          gap: 20px;
          margin-top: 20px;
        }
        .section {
          border: 1px solid ${theme.lightGreyDark};
          border-radius: ${theme.cardBorderRadius};
          padding: 15px;
          width: 100%;
        }
        .section-header {
          align-items: center;
          display: flex;
          gap: 10px;
        }
        .section-title {
          color: ${theme.textColor};
          font-size: 1.6rem;
          margin: 0;
        }
        .section-items {
          margin: 10px 0 0;
          padding-left: 22px;
        }
        .section-item {
          color: ${theme.textColorLight};
          font-size: 1.5rem;
          padding: 5px 0;
        }
        .section-empty {
          color: ${theme.textColorLighter};
          font-size: 1.5rem;
          margin-top: 10px;
        }
        .trends {
          display: flex;
          flex-direction: column;
          gap: 20px;
          margin-top: 15px;
        }
        .trend {
          border: 1px solid ${theme.lightGreyDark};
          border-radius: ${theme.cardBorderRadius};
          padding: 15px;
        }
        .trend-header {
          align-items: center;
          display: flex;
          flex-wrap: wrap;
          justify-content: space-between;
          gap: 15px 50px;
        }
        .trend-title {
          color: ${theme.textColorLight};
          font-size: 1.6rem;
          margin: 0;
          padding: 0 5px;
        }
        .insights {
          display: flex;
          flex-wrap: wrap;
          flex-direction: row;
          gap: 20px;
          margin-top: 25px;
        }
        .insight {
          background-color: ${theme.lightGreyLighter};
          border-radius: ${theme.cardBorderRadius};
          padding: 15px;
          width: 100%;
        }
        .insight-description {
          color: ${theme.textColorLight};
          font-size: 1.4rem;
          font-weight: 600;
        }
        .insight-frequency {
          margin-top: 20px;
        }
        @media (min-width: ${breakpoints.small}px) {
          .section {
            width: calc(50% - 10px);
          }
          .insight {
            width: calc(50% - 10px);
          }
        }
        @media (min-width: ${breakpoints.large}px) {
          .insight {
            width: calc(33.333% - 13.333px);
          }
        }
        @media (min-width: ${breakpoints.huge}px) {
          .section {
            width: calc(25% - 15px);
          }
        }
      `}</style>
    </div>
  );
};

export default ConversationAnalysisTrends;
